import { StandardPageLayout } from "@/components/layouts/standard-page-layout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  QueryFunctionContext,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import ConfirmationModal from "components/modals/confirmation-modal";
import CustomTable from "components/shared/table";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import * as LocalStorageKeys from "data/local-storage-keys";
import { deleteClip } from "data/mutations-common";
import { fetchClipsPaginated } from "data/queries-common";
import { Form, Formik, useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { resolveIdentityId } from "utils/identity";
import { useLocalStorage } from "utils/local-storage";
import { MultiSelectFormControl } from "../../share-component/multi-select";
import { ClipConfiguration, Status } from "../clip-ui/models";
import { ClipDetailSidebar } from "./clip-detail-sidebar";
import { getClipManagementColumns } from "./columns";
import { ColumnVisibilityMenu } from "./menus";

const NON_EDITABLE_STATUSES: Status[] = ["Completed", "Failed"];

interface ClipFilterValues {
  searchQuery: string;
  statusFilter: string[];
  authorFilter: string[];
}

const initialFilterValues: ClipFilterValues = {
  searchQuery: "",
  statusFilter: [],
  authorFilter: [],
};

interface ClipFiltersProps {
  uniqueStatuses: string[];
  uniqueAuthors: string[];
  onClear: () => void;
}

const ClipFilters = ({
  uniqueStatuses,
  uniqueAuthors,
  onClear,
}: ClipFiltersProps) => {
  const { values, handleChange, handleSubmit } =
    useFormikContext<ClipFilterValues>();

  const hasActiveFilters =
    values.searchQuery !== "" ||
    values.statusFilter.length > 0 ||
    values.authorFilter.length > 0;

  return (
    <div className="flex gap-2 items-end flex-1 min-w-0">
      <div className="flex flex-col gap-1.5 w-55">
        <Label htmlFor="searchQuery">Search</Label>
        <Input
          id="searchQuery"
          name="searchQuery"
          placeholder="Search by name or client ref..."
          value={values.searchQuery}
          onChange={(e) => {
            handleChange(e);
            handleSubmit();
          }}
        />
      </div>
      <div className="w-50">
        <MultiSelectFormControl
          id="statusFilter"
          label="Status"
          options={uniqueStatuses}
          value={values.statusFilter}
          handleSubmit={handleSubmit}
        />
      </div>
      <div className="w-50">
        <MultiSelectFormControl
          id="authorFilter"
          label="Author"
          options={uniqueAuthors}
          value={values.authorFilter}
          handleSubmit={handleSubmit}
        />
      </div>
      {hasActiveFilters && (
        <Button
          variant="link"
          size="sm"
          type="button"
          onClick={onClear}
          className="shrink-0"
        >
          Clear
        </Button>
      )}
    </div>
  );
};

export const ClipManagementPage = () => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const { enqueueInfoSnackbar } = useCustomSnackbars();
  const queryClient = useQueryClient();

  const [selectedClip, setSelectedClip] = useState<ClipConfiguration | null>(
    null,
  );
  const [filterState, setFilterState] =
    useState<ClipFilterValues>(initialFilterValues);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [clipToDelete, setClipToDelete] = useState<ClipConfiguration | null>(
    null,
  );

  // Column visibility state synced with localStorage
  const [columnVisibility, setColumnVisibility] = useLocalStorage<
    Record<string, boolean>
  >(LocalStorageKeys.clipManagementColumnVisibility, {
    name: true,
    premium: true,
    clientReference: true,
    totalCredits: true,
    totalDollars: true,
    status: true,
    author: true,
    lastEditedAt: true,
    purchasedOn: true,
    completedOn: true,
    actions: true,
  });

  const clipsQuery = useInfiniteQuery({
    queryKey: [QueryKeys.clipsKey, identityId, "management", "withCredits"],
    queryFn: async ({ pageParam }: QueryFunctionContext) => {
      const limit = 50;
      const pageNumber = pageParam ? parseInt(pageParam as string) : 0;
      return fetchClipsPaginated(pageNumber, limit, true);
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? JSON.stringify(lastPage.lastEvaluatedKey) : undefined,
    refetchInterval: 30000,
    initialPageParam: "0",
  });

  const clipDeleteMutation = useMutation({
    mutationFn: () => {
      if (!clipToDelete) throw new Error("No clip selected for deletion");
      return deleteClip(clipToDelete.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.clipsKey] });
      setDeleteModalIsOpen(false);
      setClipToDelete(null);
      if (selectedClip?.id === clipToDelete?.id) {
        setSelectedClip(null);
      }
      enqueueInfoSnackbar("Clip deleted successfully", "success");
    },
    onError: (error: Error) => {
      enqueueInfoSnackbar(error.message, "error");
    },
  });

  const allClips = useMemo(() => {
    return clipsQuery.data?.pages.flatMap((page) => page.clips) ?? [];
  }, [clipsQuery.data]);

  useEffect(() => {
    if (
      clipsQuery.hasNextPage &&
      !clipsQuery.isFetchingNextPage &&
      !clipsQuery.isPending
    ) {
      clipsQuery.fetchNextPage();
    }
  }, [
    clipsQuery.hasNextPage,
    clipsQuery.isFetchingNextPage,
    clipsQuery.isPending,
  ]);

  const uniqueStatuses = useMemo(() => {
    const statuses = new Set(allClips.map((clip) => clip.status));
    return Array.from(statuses);
  }, [allClips]);

  const uniqueAuthors = useMemo(() => {
    const authors = new Set(
      allClips
        .map((clip) => clip.authorName || clip.developer || "Unknown")
        .filter(Boolean),
    );
    return Array.from(authors);
  }, [allClips]);

  const filteredClips = useMemo(() => {
    const { searchQuery, statusFilter, authorFilter } = filterState;
    return allClips.filter((clip) => {
      const matchesSearch =
        clip.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        clip.clientReference?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter.length === 0 || statusFilter.includes(clip.status);

      const clipAuthor = clip.authorName || clip.developer || "Unknown";
      const matchesAuthor =
        authorFilter.length === 0 || authorFilter.includes(clipAuthor);

      return matchesSearch && matchesStatus && matchesAuthor;
    });
  }, [allClips, filterState]);

  const handleRowClick = (row: ClipConfiguration) => {
    setSelectedClip(row);
  };

  const handleColumnToggle = (column: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const handleDelete = useCallback((clip: ClipConfiguration) => {
    setClipToDelete(clip);
    setDeleteModalIsOpen(true);
  }, []);

  const columns = useMemo(
    () =>
      getClipManagementColumns({
        nonEditableStatuses: NON_EDITABLE_STATUSES,
        onDelete: handleDelete,
      }),
    [handleDelete],
  );

  const rows = filteredClips.map((clip) => ({
    ...clip,
    author: clip.authorName || clip.developer || "",
  }));

  return (
    <StandardPageLayout>
      {selectedClip && (
        <Alert variant="warning" className="xl:hidden">
          <AlertDescription>
            Increase screen size to view clip details
          </AlertDescription>
        </Alert>
      )}
      <div className="flex gap-4 flex-1 min-w-0">
        {/* Left side - Table */}
        <div className="flex flex-col gap-4 flex-1 min-w-0 basis-2/3">
          <Formik<ClipFilterValues>
            initialValues={filterState}
            onSubmit={(values) => setFilterState(values)}
            enableReinitialize
          >
            <Form className="w-full">
              <div className="flex gap-2 items-end justify-between">
                <ClipFilters
                  uniqueStatuses={uniqueStatuses}
                  uniqueAuthors={uniqueAuthors}
                  onClear={() => setFilterState(initialFilterValues)}
                />
                <ColumnVisibilityMenu
                  columnVisibility={columnVisibility}
                  onToggle={handleColumnToggle}
                />
              </div>
            </Form>
          </Formik>

          <CustomTable
            rows={rows}
            columns={columns}
            isLoading={clipsQuery.isPending}
            overlayText="No clips found"
            onRowClick={handleRowClick}
            getRowId={(row: ClipConfiguration) => row.id}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
          />
        </div>

        {/* Right side - Detail Sidebar (hidden on small screens via CSS) */}
        {selectedClip && (
          <div className="hidden xl:flex basis-1/3 min-w-0">
            <ClipDetailSidebar
              clip={selectedClip}
              onClose={() => setSelectedClip(null)}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isLoading={clipDeleteMutation.isPending}
        isError={clipDeleteMutation.isError}
        open={deleteModalIsOpen}
        header="Delete this clip?"
        body="This clip will be permanently removed from your organisation. You won't be able to recover its content"
        leftButtonText="CANCEL"
        leftButtonColor="primary"
        rightButtonText="DELETE CLIP"
        rightButtonColor="error"
        handleLeftButton={() => {
          setDeleteModalIsOpen(false);
          setClipToDelete(null);
        }}
        handleRightButton={() => clipDeleteMutation.mutate()}
        handleClose={() => {
          setDeleteModalIsOpen(false);
          setClipToDelete(null);
        }}
        type="Clip"
      />
    </StandardPageLayout>
  );
};
